import { useAtomValue } from "jotai/react";
import { useCallback } from "react";
import { imageLoadingStrategyAtom } from "utilities/atoms/image-loading-strategy-atom";
import { ImgTagInfo } from "utilities/parsers/image-tag-info";
import SharedPageRemoteAccessDetailsService from "utilities/services/public/pages/shared-page-remote-access-details-service";
import RemoteAccessDetailsService from "utilities/services/remote-access-details-service";

export enum ImageTagAttr {
    DataStorageContainer = "data-storage-container",
    DataFileId = "data-file-id",
    DataRelativeProviderPath = "data-relative-provider-path",
    DataSrc = "data-src",
    Src = "src",
}

export default function useQuillImageTagSanitizer() {
    const extractImgDataTags = (html: string): ImgTagInfo[] => {
        // Find all <img ...> tags (self-closing or not)
        const imgTagRegex = /<img\b[^>]*?>/gi;

        // Capture attributes inside the img tag
        const attrRegex = /([\w:-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'))?/g;

        const results: ImgTagInfo[] = [];
        let tagMatch;

        while ((tagMatch = imgTagRegex.exec(html)) !== null) {
            const tag = tagMatch[0];
            const start = tagMatch.index;
            const end = start + tag.length;

            let attrMatch: RegExpExecArray | null = null;
            const imageTagInfo = { tag, start, end } as ImgTagInfo;

            while ((attrMatch = attrRegex.exec(tag)) !== null) {
                const name = attrMatch[1];
                const value = attrMatch[2] ?? attrMatch[3] ?? true; // boolean attribute support

                if (typeof value === "string") {
                    switch (name) {
                        case ImageTagAttr.DataStorageContainer:
                            imageTagInfo.storageContainer = value;
                            break;
                        case ImageTagAttr.DataFileId:
                            imageTagInfo.fileId = Number(value) ?? -1;
                            break;
                        case ImageTagAttr.DataRelativeProviderPath:
                            imageTagInfo.relativeProviderPath = value;
                            break;
                        case ImageTagAttr.DataSrc:
                        case ImageTagAttr.Src:
                            imageTagInfo.src = value;
                            break;
                        default:
                            break;
                    }
                }
            }
            results.push(imageTagInfo);
        }

        return results;
    };
    const imageLoadingStrategy = useAtomValue(imageLoadingStrategyAtom);

    const sanitizeImageRichTextContent = (richText: string): string => {
        if (richText == null) {
            return "";
        }

        const imageTags = extractImgDataTags(richText);

        let updatedBodyDraft = richText;
        imageTags.forEach((imgTag) => {
            if (imgTag.fileId == null || imgTag.fileId === -1) {
                //Catch any remaining tags and update them:
                updatedBodyDraft = updatedBodyDraft.replace(
                    `src="${imgTag.src}"`,
                    `data-src="${imgTag.src}"`
                );
            } else {
                updatedBodyDraft = updatedBodyDraft.replace(
                    `src="${imgTag.src}"`,
                    ""
                );
            }
        });
        return updatedBodyDraft;
    };

    const restoreImageRichTextContent = useCallback(
        async (
            richText: string,
            pageGuid?: string,
            recipientGuid?: string
        ): Promise<string> => {
            if (richText == null) return "";
            //parse out the img tag properties:
            //foreach img tag, replace the data attribute with the src attribute
            const imageTags = extractImgDataTags(richText);
            return await updateRichText(
                richText,
                imageTags,
                pageGuid,
                recipientGuid
            );
        },
        []
    );

    const updateRichText = async (
        richText: string,
        imageTags: ImgTagInfo[],
        pageGuid?: string,
        recipientGuid?: string
    ) => {
        // 2. Fetch all files in parallel
        const fileResults = await Promise.all(
            imageTags.map(async (tag) => {
                if (
                    tag.fileId === -1 ||
                    !tag.storageContainer ||
                    !tag.relativeProviderPath
                ) {
                    return {
                        fileId: -1,
                        src: tag.src || "",
                    };
                }
                //TODO:  Refactor against useImageFilePath hook to avoid code duplication?
                const remoteAccessDetailsResult = pageGuid
                    ? await SharedPageRemoteAccessDetailsService.get(
                          {
                              pageGuid,
                          },
                          {
                              recipientGuid,
                              storageContainer: tag.storageContainer,
                              relativeProviderPath: tag.relativeProviderPath,
                          }
                      )
                    : await RemoteAccessDetailsService.get(undefined, {
                          storageContainer: tag.storageContainer,
                          relativeProviderPath: tag.relativeProviderPath!,
                      });
                const src = remoteAccessDetailsResult.resultObject?.url ?? "";
                return {
                    fileId: tag.fileId,
                    storageContainer: tag.storageContainer,
                    relativeProviderPath: tag.relativeProviderPath,
                    src: src,
                };
            })
        );

        let updatedRichText = richText;
        fileResults.forEach((fileInfo) => {
            if (fileInfo == null) return;
            const fileIdTag = `data-file-id="${fileInfo.fileId}"`;
            updatedRichText = updatedRichText.replace(
                fileIdTag,
                `${fileIdTag} src="${fileInfo.src}" loading="${imageLoadingStrategy}" decoding="async"`
            );
            if (fileInfo.fileId === -1) {
                //Catch any remaining tags and update them:
                updatedRichText = updatedRichText.replace(
                    `data-src="${fileInfo.src}"`,
                    `src="${fileInfo.src}" loading="${imageLoadingStrategy}" decoding="async"`
                );
            }
        });

        return updatedRichText;
    };

    return { sanitizeImageRichTextContent, restoreImageRichTextContent };
}
